import { useCallback, useEffect, useState } from "react";
import type { Paginated } from "@gatiq/shared";
import {
  Card,
  EmptyState,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeleton,
  Toolbar,
  ToolbarCount,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";

import { ListFilters, useListFilters } from "@/components/ListFilters";
import { auditActionLabel, auditEntityLabel, auditFilterLabels } from "./auditLabels";
const PAGE_SIZE = 25;

interface AuditRow {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  actorId: string | null;
  actorName: string | null;
  actorRole: string | null;
  meta: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string | null;
}

export function OrgAuditPage() {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<AuditRow> | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(query);
      if (q.trim()) query.set("q", q.trim());
      const result = await api.get<Paginated<AuditRow>>(`/org/audit?${query.toString()}`);
      setData(result);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load the audit log.");
    } finally {
      setLoading(false);
    }
  }, [page, q, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Page bleed>
      <PageHeader title="Audit log" />

      <Card fill>
        <p className="border-b border-rule px-4 py-3 text-sm text-muted">
          See who changed something, what they did, and which record was affected. Use the filters to narrow the activity.
        </p>
        <Toolbar trailing={data ? <ToolbarCount count={data.total} noun="entry" /> : null}>
          <SearchInput
            label="Search audit log"
            onChange={(value) => {
              setQ(value);
              setPage(1);
            }}
            placeholder="Search a person, activity or record…"
            value={q}
          />
          <ListFilters definitions={auditFilterLabels(data?.filters)} filters={filters} />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={4} rows={8} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState message="Actions your team takes will show up here." title="No activity yet" />
          </div>
        ) : (
          <Table fill searchable={false} filterable={false}>
            <THead>
              <TR>
                <TH>When</TH>
                <TH>Activity</TH>
                <TH>Performed by</TH>
                <TH>Affected record</TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((row) => (
                <TR key={row.id}>
                  <TD className="whitespace-nowrap text-sm text-muted">
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                  </TD>
                  <TD className="text-sm font-medium text-ink"><span title={row.action}>{auditActionLabel(row.action)}</span></TD>
                  <TD>
                    {row.actorName ?? "—"}
                    {row.actorRole ? <span className="ml-1 text-xs text-muted">({row.actorRole})</span> : null}
                  </TD>
                  <TD className="text-sm text-muted">{auditEntityLabel(row.entityType)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        {data ? (
          <div className="shrink-0">
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </div>
        ) : null}
      </Card>
    </Page>
  );
}
